'use client';

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { FormEvent, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  ArrowUpRight,
  BriefcaseBusiness,
  Bot,
  ChevronDown,
  Coins,
  CreditCard,
  ExternalLink,
  List as ListIcon,
  Maximize2,
  MessageSquarePlus,
  Minimize2,
  Minus,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  RefreshCw,
  Send,
  Settings2,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  ButtonBase,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { DEFAULT_AI_MODEL_ID, type AiModelId } from '@/ai/model/model-catalog';
import { modelLabel, reasoningOptions } from '@/lib/ai-model-ui';
import { ModelPicker } from '@/components/chat/model-picker';
import { ThinkingSelector } from '@/components/chat/thinking-selector';
import { useOpenRouterModels } from '@/components/chat/use-openrouter-models';
import type { OpenRouterModel } from '@/ai/model/openrouter-models';
import { authConfig } from '@/config/auth-config';
import { CollapsibleToolRenderer, toolDisplayName, type RendererDescriptor } from '@/components/chat/tool-renderers';
import { getResponseActions } from '@/components/chat/response-actions';
import { MarkdownContent } from '@/components/chat/markdown-content';
import { shortDate } from '@/libs/chat-date';
import { resolveChatScreenContext, type ChatScreenContext } from '@/ai/screens/chat-screen-context';

type ChatVariant = 'page' | 'drawer';

export type ChatSurfaceCopy = {
  title: string;
  subtitle: string;
  contextLabel: string;
  contextDescription: string;
};

export const getChatSurfaceCopy = (
  variant: ChatVariant,
  hasActivePortfolio: boolean,
  screenContext?: ChatScreenContext,
): ChatSurfaceCopy => {
  if (variant === 'drawer' && hasActivePortfolio) {
    return {
      title: 'Copiloto',
      subtitle: 'Pergunte usando os dados desta carteira',
      contextLabel: 'Esta carteira',
      contextDescription:
        'Os dados desta carteira estão disponíveis para perguntas sobre patrimônio, posições, dividendos e diversificação.',
    };
  }

  if (variant === 'drawer') {
    return {
      title: 'Copiloto',
      subtitle:
        screenContext && screenContext.id !== 'general'
          ? `Pergunte sobre ${screenContext.label}`
          : 'Pergunte sobre seus investimentos',
      contextLabel: screenContext?.label ?? 'Contexto geral',
      contextDescription:
        screenContext?.description ??
        'O Copiloto pode consultar fundos, mercado, relatórios e suas carteiras quando você pedir.',
    };
  }

  return {
    title: 'Copiloto',
    subtitle: 'Sua mesa de investigação',
    contextLabel: hasActivePortfolio ? 'Esta carteira' : 'Contexto geral',
    contextDescription: hasActivePortfolio
      ? 'Os dados desta carteira estão disponíveis para as próximas perguntas.'
      : 'Nesta área geral, o Copiloto consulta fundos, mercado, relatórios e suas carteiras quando você pedir.',
  };
};

export const getChatQuickPrompts = (variant: ChatVariant, hasActivePortfolio: boolean): string[] => {
  if (variant !== 'drawer') return [];
  if (hasActivePortfolio) {
    return [
      'Como está a diversificação da minha carteira?',
      'Quais FIIs pagaram mais dividendos?',
      'Compare os ativos da minha carteira',
    ];
  }
  return ['Como está o mercado de FIIs?', 'Quais indicadores devo acompanhar?', 'Compare dois FIIs'];
};

const CHAT_WELCOME_PROMPTS = [
  'Quais indicadores devo acompanhar?',
  'Como comparar dois FIIs?',
  'O que devo observar em um relatório?',
] as const;

export const getChatWelcomePrompts = (): string[] => [...CHAT_WELCOME_PROMPTS];

type ChatMessage = {
  id: string;
  role: 'USER' | 'ASSISTANT';
  status: 'COMPLETE' | 'PARTIAL' | 'FAILED';
  content: string;
  modelId: string | null;
  createdAt: string;
  renderers?: RendererDescriptor[];
};

type ConversationSummary = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastMessage: {
    content: string;
    role: ChatMessage['role'];
    createdAt: string;
  } | null;
};

type ConversationDetail = {
  id: string;
  title: string;
  thinkingEffort: string | null;
  totalInputTokens: number | null;
  totalOutputTokens: number | null;
  totalCachedInputTokens: number | null;
  totalCacheWriteTokens: number | null;
  totalCostUsd: string | null;
  totalCostBrl: string | null;
  messages: ChatMessage[];
};

type ConversationsResponse = {
  conversations: ConversationSummary[];
  isAdmin: boolean;
};
type ConversationResponse = { conversation: ConversationDetail };
type ChatToolCatalogEntry = { name: string; description: string };
type ChatToolsResponse = { tools: ChatToolCatalogEntry[] };

const fetcher = async <T,>(url: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Não foi possível carregar o chat.');
  return response.json() as Promise<T>;
};

const makeTitle = (content: string) => {
  const compact = content.trim().replace(/\s+/g, ' ');
  return compact.length > 48 ? `${compact.slice(0, 48).trim()}…` : compact;
};

const formatTokens = (value: number) => {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `${m >= 100 ? String(Math.round(m)) : m.toFixed(1).replace('.', ',')}M`;
  }
  if (value >= 1_000) {
    const k = value / 1_000;
    return `${k >= 100 ? String(Math.round(k)) : k.toFixed(1).replace('.', ',')}k`;
  }
  return String(value);
};

const formatCreditCents = (value = 0) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);

const formatChatCredits = (credits?: { monthlyCents: number; purchasedCents: number }) =>
  formatCreditCents((credits?.monthlyCents ?? 0) + (credits?.purchasedCents ?? 0));

export function CreditBalanceSummary({
  credits,
  compact = false,
}: {
  credits?: { monthlyCents: number; purchasedCents: number };
  compact?: boolean;
}) {
  const rows = [
    { label: 'Saldo da assinatura', value: formatCreditCents(credits?.monthlyCents) },
    { label: 'Créditos adicionais', value: formatCreditCents(credits?.purchasedCents) },
    { label: 'Disponível total', value: formatChatCredits(credits), strong: true },
  ];

  return (
    <Stack spacing={compact ? 0.35 : 0.55} sx={{ mt: 1 }}>
      {rows.map((row) => (
        <Stack
          key={row.label}
          direction='row'
          justifyContent='space-between'
          alignItems='baseline'
          spacing={1}
          sx={row.strong ? { pt: 0.55, borderTop: '1px solid hsl(var(--border))' } : undefined}
        >
          <Typography variant='caption' color='text.secondary'>
            {row.label}
          </Typography>
          <Typography variant='caption' sx={{ fontWeight: row.strong ? 850 : 700, color: 'hsl(var(--foreground))' }}>
            {row.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

const CHAT_SCROLL_BOTTOM_THRESHOLD = 56;
const MOBILE_USAGE_PANEL_ID = 'chat-mobile-usage-panel';
const LEFT_SIDEBAR_STORAGE_KEY = 'chat-left-sidebar-collapsed';
const RIGHT_SIDEBAR_STORAGE_KEY = 'chat-right-sidebar-collapsed';

function SidebarCollapseButton({
  side,
  collapsed,
  onClick,
}: {
  side: 'left' | 'right';
  collapsed: boolean;
  onClick: () => void;
}) {
  const expanding = collapsed;
  const label = `${expanding ? 'Expandir' : 'Recolher'} ${side === 'left' ? 'histórico de conversas' : 'configurações do Copiloto'}`;
  const Icon =
    side === 'left' ? (expanding ? PanelLeftOpen : PanelLeftClose) : expanding ? PanelRightOpen : PanelRightClose;

  return (
    <Tooltip title={label} placement={side === 'left' ? 'right' : 'left'}>
      <IconButton
        onClick={onClick}
        aria-label={label}
        sx={{
          width: 32,
          height: 32,
          borderRadius: 0,
          color: 'hsl(var(--muted-foreground))',
          justifyContent: side === 'left' ? 'flex-end' : 'flex-start',
          alignItems: 'flex-start',
          p: 0,
          touchAction: 'manipulation',
          transition: 'background-color 160ms ease, color 160ms ease, border-color 160ms ease',
          '&:hover': {
            bgcolor: 'transparent',
            color: 'hsl(var(--foreground))',
            '& [data-collapse-visual]': {
              bgcolor: 'hsl(var(--muted) / 0.55)',
            },
          },
          '&:focus-visible': {
            outline: '3px solid hsl(var(--ring) / 0.55)',
            outlineOffset: 2,
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
        }}
      >
        <Box
          data-collapse-visual
          sx={{
            width: 19,
            height: 19,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 1,
            transition: 'background-color 160ms ease',
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          }}
        >
          <Icon size={19} aria-hidden='true' />
        </Box>
      </IconButton>
    </Tooltip>
  );
}

type ScrollViewport = { scrollTop: number; scrollHeight: number };

export const scrollMessagesToBottom = (viewport: ScrollViewport | null | undefined, shouldFollow: () => boolean) => {
  if (!viewport || !shouldFollow()) return;
  viewport.scrollTop = viewport.scrollHeight;
};

type AutoScrollScheduler = {
  schedule: () => void;
  cancel: () => void;
};

type SchedulerTimeoutHandle = ReturnType<typeof setTimeout>;

type SchedulerGlobals = {
  ResizeObserver?: typeof ResizeObserver;
  requestAnimationFrame: (callback: FrameRequestCallback) => number;
  cancelAnimationFrame: (handle: number) => void;
  setTimeout: (callback: () => void, delay?: number) => SchedulerTimeoutHandle;
  clearTimeout: (handle: SchedulerTimeoutHandle) => void;
};

export const createMessagesAutoScrollScheduler = ({
  viewport,
  shouldFollow,
  content,
  fallbackDelayMs = 120,
  globals,
}: {
  viewport: { current: ScrollViewport | null };
  shouldFollow: { current: boolean };
  content: { current: Element | null };
  fallbackDelayMs?: number;
  globals: SchedulerGlobals;
}): AutoScrollScheduler => {
  let frame: number | null = null;
  let timeout: SchedulerTimeoutHandle | null = null;
  let observer: ResizeObserver | null = null;

  const cancelScheduled = () => {
    if (frame !== null) {
      globals.cancelAnimationFrame(frame);
      frame = null;
    }
    if (timeout !== null) {
      globals.clearTimeout(timeout);
      timeout = null;
    }
  };

  const performScroll = () => {
    scrollMessagesToBottom(viewport.current, () => shouldFollow.current);
  };

  const scheduleRaf = () => {
    cancelScheduled();
    frame = globals.requestAnimationFrame(() => {
      frame = null;
      performScroll();
    });
  };

  const ResizeObserverCtor = globals.ResizeObserver;

  if (ResizeObserverCtor) {
    observer = new ResizeObserverCtor(scheduleRaf);
    if (content.current) observer.observe(content.current);
    scheduleRaf();
  } else {
    scheduleRaf();
    timeout = globals.setTimeout(() => {
      timeout = null;
      performScroll();
    }, fallbackDelayMs);
  }

  return {
    schedule: scheduleRaf,
    cancel: () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      cancelScheduled();
    },
  };
};

const readSseStream = async (
  response: Response,
  onEvent: (event: string, data: Record<string, unknown>) => void | Promise<void>,
) => {
  if (!response.body) throw new Error('O modelo não iniciou o streaming.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const consumeBlock = async (block: string) => {
    let event = 'message';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) event = line.slice(7).trim();
      if (line.startsWith('data: ')) data += line.slice(6);
    }
    if (!data) return;
    await onEvent(event, JSON.parse(data) as Record<string, unknown>);
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';
    for (const block of blocks) await consumeBlock(block);
    if (done) break;
  }

  if (buffer.trim()) await consumeBlock(buffer);
};

function ConversationList({
  conversations,
  activeId,
  onSelect,
  onNew,
  onRefresh,
  isRefreshing,
  collapsed,
  onToggleCollapsed,
  mobile = false,
  showCollapseButton = true,
  credits,
}: {
  conversations: ConversationSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobile?: boolean;
  showCollapseButton?: boolean;
  credits?: { monthlyCents: number; purchasedCents: number };
}) {
  if (collapsed) {
    return (
      <Box
        sx={{
          display: mobile ? 'flex' : { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          gap: 1,
          position: 'relative',
          pb: 1,
          alignItems: 'stretch',
          bgcolor: 'hsl(var(--card))',
          borderRight: '1px solid hsl(var(--border))',
        }}
      >
        {showCollapseButton ? (
          <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
            <SidebarCollapseButton side='left' collapsed onClick={onToggleCollapsed} />
          </Box>
        ) : null}
        <Tooltip
          title={`${conversations.length} ${conversations.length === 1 ? 'conversa' : 'conversas'}${activeId ? ` · ${conversations.find((conversation) => conversation.id === activeId)?.title ?? 'conversa ativa'}` : ''}`}
          placement='right'
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              pt: showCollapseButton ? 5.5 : 1,
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            <MessageSquarePlus size={16} aria-hidden='true' />
            <Typography component='span' sx={{ fontSize: 9, lineHeight: 1 }}>
              {conversations.length}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: mobile ? 'flex' : { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: 'hsl(var(--card))',
        borderRight: mobile ? 'none' : '1px solid hsl(var(--border))',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          p: 2.75,
          pr: 6.5,
          pb: 1.25,
        }}
      >
        {showCollapseButton ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            <SidebarCollapseButton side='left' collapsed={false} onClick={onToggleCollapsed} />
          </Box>
        ) : null}
        <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
          <Stack direction='row' alignItems='center' gap={1.25} minWidth={0}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
              }}
            >
              <Sparkles size={16} />
            </Box>
            <Box minWidth={0}>
              <Typography variant='h6' noWrap sx={{ fontWeight: 850, lineHeight: 1.15 }}>
                Copiloto
              </Typography>
              <Typography variant='caption' noWrap sx={{ color: 'hsl(var(--muted-foreground))' }}>
                Seu assistente de investimentos
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ px: 2.5, py: 2 }}>
        <Button
          fullWidth
          variant='outlined'
          startIcon={<MessageSquarePlus size={17} />}
          onClick={onNew}
          sx={{
            minHeight: 42,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 800,
            color: 'hsl(var(--foreground))',
            borderColor: 'hsl(var(--primary) / 0.24)',
            bgcolor: 'hsl(var(--accent) / 0.5)',
            '&:hover': {
              borderColor: 'hsl(var(--primary) / 0.5)',
              bgcolor: 'hsl(var(--accent))',
            },
          }}
        >
          Nova conversa
        </Button>
      </Box>
      <Box sx={{ px: 2.5, py: 1.25 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
          <Typography variant='body2' sx={{ fontWeight: 850 }}>
            Conversas
          </Typography>
          <Stack direction='row' spacing={0.5}>
            <Tooltip title='Recarregar conversas'>
              <IconButton aria-label='Atualizar conversas' onClick={onRefresh} disabled={isRefreshing} size='small'>
                <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : undefined} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Nova conversa'>
              <IconButton aria-label='Nova conversa' onClick={onNew} size='small'>
                <MessageSquarePlus size={17} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Stack spacing={0.5} sx={{ px: 1.25, pb: 1.25, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {conversations.map((conversation) => {
          const isActive = conversation.id === activeId;
          return (
            <Button
              key={conversation.id}
              onClick={() => onSelect(conversation.id)}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                borderRadius: 1.5,
                px: 1.25,
                py: 1.1,
                color: 'hsl(var(--foreground))',
                bgcolor: isActive ? 'hsl(var(--card))' : 'transparent',
                borderLeft: isActive ? '3px solid hsl(var(--primary))' : '3px solid transparent',
                boxShadow: isActive ? '0 8px 18px hsl(var(--foreground) / 0.06)' : 'none',
                '&:hover': { bgcolor: 'hsl(var(--card) / 0.8)' },
              }}
            >
              <Stack direction='row' spacing={1.1} sx={{ minWidth: 0, width: '100%' }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 1.5,
                    color: isActive ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                    bgcolor: isActive ? 'hsl(var(--accent))' : 'hsl(var(--muted) / 0.55)',
                  }}
                >
                  <BriefcaseBusiness size={16} aria-hidden='true' />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction='row' justifyContent='space-between' gap={1}>
                    <Typography variant='body2' noWrap sx={{ fontWeight: 750 }}>
                      {conversation.title}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'hsl(var(--muted-foreground))' }}>
                      {shortDate(conversation.updatedAt)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant='caption'
                    noWrap
                    sx={{ display: 'block', mt: 0.35, color: 'hsl(var(--muted-foreground))' }}
                  >
                    {conversation.lastMessage?.content ?? 'Sem mensagens'}
                  </Typography>
                </Box>
              </Stack>
            </Button>
          );
        })}
        {!conversations.length ? (
          <Typography variant='caption' sx={{ p: 1.25, color: 'hsl(var(--muted-foreground))' }}>
            Crie uma conversa para começar.
          </Typography>
        ) : null}
      </Stack>
      <Box sx={{ mt: 'auto', p: 2, pt: 1.25 }}>
        <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 2, bgcolor: 'hsl(var(--background))' }}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box
              sx={{
                width: 30,
                height: 30,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                bgcolor: 'hsl(var(--accent))',
                color: 'hsl(var(--primary))',
              }}
            >
              <Coins size={16} aria-hidden='true' />
            </Box>
            <Box>
              <Typography variant='body2' sx={{ fontWeight: 800 }}>
                Créditos IA
              </Typography>
            </Box>
          </Stack>
          <CreditBalanceSummary credits={credits} compact />
          <Button
            component={Link}
            href='/configuracoes/faturamento#adicionar-creditos'
            size='small'
            sx={{ mt: 0.75, px: 0, textTransform: 'none', fontWeight: 750 }}
          >
            Comprar créditos
          </Button>
        </Paper>
        <Button
          component={Link}
          href='/configuracoes'
          startIcon={<Settings2 size={16} />}
          sx={{ mt: 1, px: 1, color: 'hsl(var(--foreground))', textTransform: 'none' }}
        >
          Configurações
        </Button>
      </Box>
    </Box>
  );
}

function ContextMeter({
  modelId,
  models,
  lastInputTokens,
}: {
  modelId: string;
  models: OpenRouterModel[];
  lastInputTokens: number | null | undefined;
}) {
  const model = models.find((candidate) => candidate.id === modelId);
  if (!model || model.contextLength === null) {
    return (
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          mt: 1.5,
          color: 'hsl(var(--muted-foreground))',
        }}
      >
        Contexto: dinâmico (definido pelo roteador)
      </Typography>
    );
  }
  const used = lastInputTokens ?? null;
  const ratio = used === null ? 0 : (used / model.contextLength) * 100;
  const isOverflow = used !== null && used >= model.contextLength;
  const isWarning = !isOverflow && used !== null && used >= model.contextLength * 0.9;
  const color = isOverflow ? 'hsl(var(--destructive))' : isWarning ? 'hsl(var(--secondary))' : 'hsl(var(--primary))';

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='baseline' gap={1}>
        <Typography variant='caption' sx={{ color: 'hsl(var(--muted-foreground))' }}>
          Contexto
        </Typography>
        <Typography
          variant='caption'
          sx={{
            color: 'hsl(var(--foreground))',
            fontWeight: 750,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {used === null ? '—' : `~${formatTokens(used)}`} / {formatTokens(model.contextLength)}
        </Typography>
      </Stack>
      <Box
        role='progressbar'
        aria-label='Uso do contexto da conversa'
        aria-valuemin={0}
        aria-valuemax={model.contextLength}
        aria-valuenow={used ?? 0}
        sx={{
          mt: 0.65,
          height: 4,
          borderRadius: 2,
          bgcolor: 'hsl(var(--muted))',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${Math.min(100, Math.max(0, ratio))}%`,
            height: '100%',
            bgcolor: color,
          }}
        />
      </Box>
      {isOverflow ? (
        <Typography variant='caption' sx={{ display: 'block', mt: 0.75, color, lineHeight: 1.35 }}>
          Esta conversa excede o contexto deste modelo. Troque para um modelo maior ou crie uma nova conversa.
        </Typography>
      ) : isWarning ? (
        <Typography variant='caption' sx={{ display: 'block', mt: 0.75, color, lineHeight: 1.35 }}>
          O contexto desta conversa está quase cheio.
        </Typography>
      ) : null}
    </Box>
  );
}

export function UsageMetrics({
  inputTokens,
  outputTokens,
  cachedInputTokens,
  cacheWriteTokens,
  costUsd,
  costBrl,
}: {
  inputTokens: number | null | undefined;
  outputTokens: number | null | undefined;
  cachedInputTokens: number | null | undefined;
  cacheWriteTokens: number | null | undefined;
  costUsd?: string | null | undefined;
  costBrl?: string | null | undefined;
}) {
  const hasUsageData =
    inputTokens != null ||
    outputTokens != null ||
    cachedInputTokens != null ||
    cacheWriteTokens != null ||
    costUsd != null ||
    costBrl != null;

  if (!hasUsageData) {
    return (
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          mt: 1.25,
          color: 'hsl(var(--muted-foreground))',
        }}
      >
        Ainda não há dados de uso
      </Typography>
    );
  }

  const metrics = [
    {
      label: 'Entrada',
      value: inputTokens,
    },
    {
      label: 'Saída',
      value: outputTokens,
    },
    {
      label: (
        <Tooltip title='Total de tokens de entrada reutilizados do cache do OpenRouter nesta conversa'>
          <Typography component='span' variant='caption'>
            Cache read
          </Typography>
        </Tooltip>
      ),
      value: cachedInputTokens,
    },
    {
      label: (
        <Tooltip title='Total de tokens escritos no cache do OpenRouter nesta conversa'>
          <Typography component='span' variant='caption'>
            Cache write
          </Typography>
        </Tooltip>
      ),
      value: cacheWriteTokens,
    },
    {
      label: 'Custo',
      value:
        costBrl == null
          ? null
          : new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Number(costBrl)),
    },
  ];

  return (
    <Box component='section' aria-label='Uso da conversa' sx={{ mt: 1.25 }}>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          mb: 0.75,
          color: 'hsl(var(--muted-foreground))',
          fontWeight: 700,
        }}
      >
        Uso da conversa
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 0.75,
        }}
      >
        {metrics.map((metric, index) => (
          <Box
            key={index}
            sx={{
              minWidth: 0,
              px: 0.9,
              py: 0.75,
              border: '1px solid hsl(var(--border))',
              borderRadius: 1.25,
              bgcolor: 'hsl(var(--muted) / 0.28)',
            }}
          >
            <Typography
              component='div'
              variant='caption'
              sx={{
                overflow: 'hidden',
                color: 'hsl(var(--muted-foreground))',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {metric.label}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                mt: 0.25,
                color: 'hsl(var(--foreground))',
                fontWeight: 750,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
              }}
            >
              {metric.value == null
                ? '—'
                : typeof metric.value === 'number'
                  ? formatTokens(metric.value)
                  : metric.value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75 }}>
        % do input em cache:{' '}
        {inputTokens != null && inputTokens > 0
          ? `${(((cachedInputTokens ?? 0) / inputTokens) * 100).toFixed(1)}%`
          : '—'}
      </Typography>
    </Box>
  );
}

export function ConversationUsageDisclosure({
  expanded,
  onToggle,
  inputTokens,
  outputTokens,
  cachedInputTokens,
  cacheWriteTokens,
  costUsd,
  costBrl,
}: {
  expanded: boolean;
  onToggle: () => void;
  inputTokens: number | null | undefined;
  outputTokens: number | null | undefined;
  cachedInputTokens: number | null | undefined;
  cacheWriteTokens: number | null | undefined;
  costUsd?: string | null;
  costBrl?: string | null;
}) {
  return (
    <Box sx={{ mt: 1.25 }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        sx={{
          width: '100%',
          minHeight: 34,
          justifyContent: 'space-between',
          borderRadius: 1.25,
          px: 0.5,
          textAlign: 'left',
          '&:focus-visible': { outline: '2px solid hsl(var(--ring))', outlineOffset: 2 },
        }}
      >
        <Box>
          <Typography variant='caption' sx={{ display: 'block', fontWeight: 800 }}>
            Uso avançado da conversa
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Tokens, cache e custo desta conversa
          </Typography>
        </Box>
        <ChevronDown
          size={17}
          aria-hidden='true'
          style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : undefined }}
        />
      </ButtonBase>
      {expanded ? (
        <UsageMetrics
          inputTokens={inputTokens}
          outputTokens={outputTokens}
          cachedInputTokens={cachedInputTokens}
          cacheWriteTokens={cacheWriteTokens}
          costUsd={costUsd}
          costBrl={costBrl}
        />
      ) : null}
    </Box>
  );
}

// ASSISTANT messages carrying a full-width renderer (e.g. comparator) use the
// entire available column width so their grid does not overflow horizontally.
export function shouldUseFullWidth(role: ChatMessage['role'], renderers: RendererDescriptor[] | undefined): boolean {
  return role === 'ASSISTANT' && renderers?.some((renderer) => renderer.kind === 'comparator') === true;
}

// Navigation links are useful context for every answer; keep the richer
// tool output collapsed by default so the conversation remains readable.
export function isToolExpandedByDefault(descriptor: RendererDescriptor): boolean {
  return descriptor.kind === 'screen-link' || descriptor.kind === 'external-link';
}

export function ChatMessageBubble({
  message,
  variant = 'page',
  isConsulting = false,
  consultingToolName = null,
  models,
  toolExpansionOverrides,
  onToggleTool,
  onAsk,
}: {
  message: ChatMessage;
  variant?: ChatVariant;
  isConsulting?: boolean;
  consultingToolName?: string | null;
  models: OpenRouterModel[];
  toolExpansionOverrides: ReadonlyMap<string, boolean>;
  onToggleTool: (key: string, defaultExpanded: boolean) => void;
  onAsk?: (prompt: string) => void;
}) {
  const isUser = message.role === 'USER';
  const isFailed = message.status === 'FAILED';
  const isPartial = message.status === 'PARTIAL';
  const isFullWidth = shouldUseFullWidth(message.role, message.renderers);
  const isDrawer = variant === 'drawer';
  return (
    <Stack direction='row' justifyContent={isUser ? 'flex-end' : 'flex-start'} sx={{ mb: isDrawer ? 2 : 2.5 }}>
      <Box
        sx={
          isFullWidth || !isUser
            ? { width: '100%', maxWidth: '100%' }
            : { maxWidth: isDrawer ? '94%' : { xs: '94%', sm: '82%' } }
        }
      >
        <Stack
          direction='row'
          spacing={isDrawer ? 0.75 : 1}
          alignItems='center'
          sx={{ mb: 0.65 }}
          justifyContent={isUser ? 'flex-end' : 'flex-start'}
        >
          {!isUser ? (
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: isDrawer ? 20 : 22,
                height: isDrawer ? 20 : 22,
                borderRadius: '50%',
                bgcolor: 'hsl(var(--accent))',
                color: 'hsl(var(--primary))',
              }}
            >
              <Sparkles size={isDrawer ? 12 : 13} />
            </Box>
          ) : null}
          <Typography variant='caption' sx={{ color: 'hsl(var(--muted-foreground))', fontWeight: 750 }}>
            {isUser ? 'Você' : isDrawer ? 'Copiloto' : `Copiloto · ${modelLabel(message.modelId, models)}`}
          </Typography>
        </Stack>
        <Paper
          variant='outlined'
          sx={{
            p: isUser ? (isDrawer ? 1.35 : { xs: 1.5, sm: 1.75 }) : 0,
            borderRadius: isUser ? '14px 14px 4px 14px' : 0,
            bgcolor: isUser ? 'hsl(var(--muted) / 0.72)' : 'transparent',
            color: 'hsl(var(--foreground))',
            borderColor: isUser ? 'hsl(var(--border))' : 'transparent',
            boxShadow: 'none',
          }}
        >
          {isUser ? (
            <Typography variant='body2' sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
              {message.content || ' '}
            </Typography>
          ) : (
            <MarkdownContent content={message.content || ' '} />
          )}
          {isFailed ? (
            <Typography variant='body2' sx={{ color: 'error.main', lineHeight: 1.65 }}>
              A resposta falhou antes de ser concluída. Tente novamente.
            </Typography>
          ) : null}
          {isPartial ? (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
              Resposta interrompida antes da conclusão.
            </Typography>
          ) : null}
          {isConsulting ? (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
              {consultingToolName ? `Consultando ${toolDisplayName(consultingToolName)}…` : 'Consultando…'}
            </Typography>
          ) : null}
          {message.renderers?.map((descriptor, index) => {
            const toolKey = `${message.id}:${descriptor.kind}:${index}`;
            const defaultExpanded = isToolExpandedByDefault(descriptor);
            return (
              <CollapsibleToolRenderer
                key={toolKey}
                descriptor={descriptor}
                expanded={toolExpansionOverrides.get(toolKey) ?? defaultExpanded}
                onToggle={() => onToggleTool(toolKey, defaultExpanded)}
                onAsk={onAsk}
              />
            );
          })}
        </Paper>
      </Box>
    </Stack>
  );
}

function ChatQuickPrompts({
  prompts,
  disabled,
  onAsk,
}: {
  prompts: string[];
  disabled: boolean;
  onAsk: (prompt: string) => void;
}) {
  if (!prompts.length) return null;

  return (
    <Stack spacing={0.75} sx={{ mb: 1.25 }}>
      <Typography
        variant='overline'
        sx={{
          color: 'hsl(var(--secondary))',
          fontWeight: 800,
          letterSpacing: '0.1em',
          lineHeight: 1.3,
        }}
      >
        Sugestões para explorar
      </Typography>
      <Stack direction='row' flexWrap='wrap' gap={0.75}>
        {prompts.map((prompt) => (
          <Button
            key={prompt}
            variant='outlined'
            size='small'
            disabled={disabled}
            onClick={() => onAsk(prompt)}
            sx={{
              borderRadius: 999,
              px: 1.25,
              py: 0.6,
              textTransform: 'none',
              textAlign: 'left',
              lineHeight: 1.35,
              borderColor: 'hsl(var(--border))',
              bgcolor: 'hsl(var(--card) / 0.7)',
              '&:hover': {
                borderColor: 'hsl(var(--primary))',
                bgcolor: 'hsl(var(--accent))',
              },
            }}
          >
            {prompt}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

function ChatContextCard({
  variant,
  activePortfolioId,
  screenContext,
}: {
  variant: ChatVariant;
  activePortfolioId?: string;
  screenContext?: ChatScreenContext;
}) {
  const copy = getChatSurfaceCopy(variant, Boolean(activePortfolioId), screenContext);
  const isDrawer = variant === 'drawer';

  return (
    <Paper
      data-chat-context='true'
      variant='outlined'
      sx={{
        p: isDrawer ? 1.25 : 1.5,
        borderRadius: 2,
        bgcolor: 'hsl(var(--accent) / 0.58)',
        borderColor: 'hsl(var(--primary) / 0.28)',
      }}
    >
      <Typography
        variant='overline'
        sx={{
          display: 'block',
          mb: 0.75,
          color: 'hsl(var(--primary))',
          fontWeight: 850,
          letterSpacing: '0.08em',
          lineHeight: 1.25,
        }}
      >
        Contexto disponível para perguntas
      </Typography>
      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack direction='row' spacing={1} alignItems='center' minWidth={0}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              width: isDrawer ? 30 : 34,
              height: isDrawer ? 30 : 34,
              borderRadius: 1.5,
              bgcolor: 'hsl(var(--card))',
              color: 'hsl(var(--primary))',
            }}
          >
            {activePortfolioId ? (
              <BriefcaseBusiness size={isDrawer ? 16 : 17} aria-hidden='true' />
            ) : (
              <Sparkles size={isDrawer ? 16 : 17} aria-hidden='true' />
            )}
          </Box>
          <Box minWidth={0}>
            <Typography variant='body2' sx={{ fontWeight: 800 }}>
              {copy.contextLabel}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.45 }}>
              {copy.contextDescription}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ChatToolsPanel({ onOpenTools }: { onOpenTools: () => void }) {
  const groups = [
    { label: 'Fundos e comparações', description: 'Indicadores, filtros e fundos similares', icon: Bot },
    { label: 'Carteiras', description: 'Patrimônio, renda e diversificação', icon: BriefcaseBusiness },
    { label: 'Mercado', description: 'Cenário atual e séries históricas', icon: ListIcon },
    { label: 'Relatórios e web', description: 'Documentos e fontes atualizadas', icon: ExternalLink },
  ];

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 2, bgcolor: 'hsl(var(--card))' }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
        <Box>
          <Typography variant='body2' sx={{ fontWeight: 850 }}>
            Ferramentas do modelo
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Consultadas pelo Copiloto quando necessário
          </Typography>
        </Box>
        <Button onClick={onOpenTools} size='small' sx={{ minWidth: 0, p: 0, textTransform: 'none' }}>
          Ver todas
        </Button>
      </Stack>
      <Stack spacing={0.4}>
        {groups.map((group) => {
          const Icon = group.icon;
          return (
            <Button
              key={group.label}
              onClick={onOpenTools}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                gap: 1,
                px: 0.75,
                py: 0.8,
                borderRadius: 1.5,
                color: 'hsl(var(--foreground))',
                textTransform: 'none',
                textAlign: 'left',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  borderRadius: 1.25,
                  bgcolor: 'hsl(var(--accent))',
                  color: 'hsl(var(--primary))',
                }}
              >
                <Icon size={16} aria-hidden='true' />
              </Box>
              <Box minWidth={0}>
                <Typography variant='body2' sx={{ fontWeight: 750, lineHeight: 1.3 }}>
                  {group.label}
                </Typography>
                <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
                  {group.description}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Stack>
    </Paper>
  );
}

function ChatToolsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading, error } = useSWR<ChatToolsResponse>(open ? '/api/chat/tools' : null, fetcher, {
    revalidateOnFocus: false,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      aria-labelledby='chat-tools-dialog-title'
      slotProps={{ paper: { sx: { borderRadius: 3, bgcolor: 'hsl(var(--background))' } } }}
    >
      <DialogTitle id='chat-tools-dialog-title' sx={{ pb: 1 }}>
        <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap={2}>
          <Stack direction='row' spacing={1.25} alignItems='center'>
            <Box
              sx={{
                width: 38,
                height: 38,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 1.5,
                bgcolor: 'hsl(var(--accent))',
                color: 'hsl(var(--primary))',
              }}
            >
              <Settings2 size={19} aria-hidden='true' />
            </Box>
            <Box>
              <Typography variant='h6' sx={{ fontWeight: 850 }}>
                Ferramentas disponíveis
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Recursos que o modelo pode consultar para responder nesta conversa.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} aria-label='Fechar ferramentas' size='small'>
            <X size={18} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5 }}>
        {isLoading ? (
          <Stack alignItems='center' justifyContent='center' spacing={1.5} sx={{ minHeight: 180 }}>
            <CircularProgress size={28} />
            <Typography variant='body2' color='text.secondary'>
              Carregando ferramentas…
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity='error'>Não foi possível carregar as ferramentas disponíveis.</Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.25,
            }}
          >
            {(data?.tools ?? []).map((tool) => {
              const displayName = toolDisplayName(tool.name);
              return (
                <Paper key={tool.name} variant='outlined' sx={{ p: 1.5, borderRadius: 2, bgcolor: 'hsl(var(--card))' }}>
                  <Typography variant='body2' sx={{ fontWeight: 800 }}>
                    {displayName.charAt(0).toUpperCase() + displayName.slice(1)}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 0.45, lineHeight: 1.5 }}>
                    {tool.description}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChatComposer({
  onSubmit,
  disabled,
  isSending,
  thinkingEffort,
  selectedModel,
  onThinkingChange,
  onOpenTools,
  variant = 'page',
}: {
  onSubmit: (value: string) => void;
  disabled: boolean;
  isSending: boolean;
  thinkingEffort: string | null;
  selectedModel?: OpenRouterModel;
  onThinkingChange: (value: string | null) => void;
  onOpenTools: () => void;
  variant?: ChatVariant;
}) {
  const [value, setValue] = useState('');
  const isDrawer = variant === 'drawer';

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || isSending) return;
    onSubmit(trimmed);
    setValue('');
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{ mt: 'auto' }}>
      <Paper
        variant='outlined'
        sx={{
          p: 1.25,
          borderRadius: 2.5,
          bgcolor: 'hsl(var(--card))',
          borderColor: 'hsl(var(--border))',
          boxShadow: '0 10px 28px hsl(var(--foreground) / 0.06)',
          '&:focus-within': {
            borderColor: 'hsl(var(--primary) / 0.58)',
            boxShadow: '0 0 0 3px hsl(var(--primary) / 0.1), 0 12px 30px hsl(var(--foreground) / 0.08)',
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          variant='standard'
          aria-label='Mensagem para o Copiloto'
          minRows={isDrawer ? 2 : 2}
          maxRows={isDrawer ? 5 : 6}
          value={value}
          disabled={disabled}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            disabled
              ? 'Crie ou selecione uma conversa para começar...'
              : isDrawer
                ? 'Pergunte sobre sua carteira...'
                : 'Pergunte sobre FIIs, renda fixa, sua carteira ou qualquer dúvida...'
          }
          slotProps={{ input: { disableUnderline: true } }}
        />
        <Stack direction='row' justifyContent='space-between' alignItems='flex-end' gap={1} sx={{ mt: 1 }}>
          <Stack direction='row' alignItems='center' flexWrap='wrap' gap={0.75}>
            <ThinkingSelector
              model={selectedModel}
              value={thinkingEffort}
              onChange={onThinkingChange}
              disabled={disabled}
            />
            <Button
              onClick={onOpenTools}
              size='small'
              variant='outlined'
              startIcon={<Settings2 size={14} />}
              sx={{ borderRadius: 999, textTransform: 'none' }}
            >
              Ferramentas
            </Button>
            {!isDrawer ? (
              <Button
                component={Link}
                href='/carteira'
                size='small'
                variant='outlined'
                startIcon={<BriefcaseBusiness size={14} />}
                sx={{ borderRadius: 999, textTransform: 'none' }}
              >
                Minha carteira
              </Button>
            ) : null}
          </Stack>
          <IconButton
            type='submit'
            aria-label='Enviar mensagem'
            disabled={disabled || isSending || !value.trim()}
            sx={{
              flexShrink: 0,
              width: 36,
              height: 36,
              color: 'hsl(var(--primary-foreground))',
              bgcolor: 'hsl(var(--primary))',
              '&:hover': { bgcolor: 'hsl(var(--primary) / 0.9)' },
              '&.Mui-disabled': { bgcolor: 'hsl(var(--muted))' },
            }}
          >
            <Send size={17} />
          </IconButton>
        </Stack>
      </Paper>
      <Typography
        variant='caption'
        sx={{ display: { xs: 'none', md: 'block' }, mt: 0.5, textAlign: 'right', color: 'text.secondary' }}
      >
        Enter para enviar · Shift + Enter para nova linha
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          mt: 0.5,
          textAlign: 'center',
          color: 'hsl(var(--muted-foreground))',
          lineHeight: 1.35,
        }}
      >
        Conteúdo informativo. Não é recomendação de investimento.
      </Typography>
    </Box>
  );
}

function GuestChatWorkspace() {
  const loginToChatHref = authConfig.loginUrlForRedirect('/chat');
  const loginToCreditsHref = authConfig.loginUrlForRedirect('/configuracoes/faturamento#adicionar-creditos');

  return (
    <Box
      data-chat-page='true'
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'grid',
        placeItems: 'center',
        p: { xs: 2, md: 5 },
        bgcolor: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
      }}
    >
      <Paper
        variant='outlined'
        sx={{
          width: '100%',
          maxWidth: 720,
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          background:
            'linear-gradient(145deg, hsl(var(--accent) / .72), hsl(var(--accent) / .28) 64%, hsl(var(--card)) 100%)',
          borderColor: 'hsl(var(--primary) / .28)',
          boxShadow: '0 24px 70px hsl(var(--foreground) / .1)',
        }}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              width: 44,
              height: 44,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 2,
              bgcolor: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
            }}
          >
            <Sparkles size={22} aria-hidden='true' />
          </Box>
          <Typography variant='overline' color='primary' fontWeight={900}>
            Copiloto IA
          </Typography>
          <Typography component='h1' variant='h3' sx={{ fontWeight: 850, letterSpacing: '-0.035em' }}>
            Converse com seus investimentos com contexto.
          </Typography>
          <Typography color='text.secondary' sx={{ lineHeight: 1.75 }}>
            Crie sua conta para testar o Copiloto, consultar suas carteiras e usar ferramentas de fundos, relatórios e
            mercado. Depois, escolha entre o trial do Pro e créditos avulsos para continuar usando.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.25}>
            <Button
              component={Link}
              href={loginToChatHref}
              variant='contained'
              size='large'
              sx={{ textTransform: 'none', fontWeight: 850 }}
            >
              Criar conta grátis
            </Button>
            <Button
              component={Link}
              href='/precos'
              variant='outlined'
              size='large'
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Fazer trial do Pro
            </Button>
          </Stack>
          <Button
            component={Link}
            href={loginToCreditsHref}
            size='small'
            startIcon={<CreditCard size={15} aria-hidden='true' />}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0 }}
          >
            Já tem conta? Entrar para comprar créditos
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export type ChatActiveConversation = { id: string; title: string };

export function ChatWorkspace({
  variant = 'page',
  onClose,
  defaultModelId = DEFAULT_AI_MODEL_ID,
  defaultThinkingEffort = null,
  isAdmin = false,
  isGuest = false,
  initialConversationId = null,
  onActiveConversationChange,
  onActivity,
  isExpanded = false,
  onToggleExpand,
  onMinimize,
}: {
  variant?: ChatVariant;
  onClose?: () => void;
  defaultModelId?: AiModelId;
  defaultThinkingEffort?: string | null;
  isAdmin?: boolean;
  isGuest?: boolean;
  /** Drawer-only: conversation restored by the persistent launcher on mount. */
  initialConversationId?: string | null;
  /** Drawer-only: reports the active conversation so the launcher can persist it. */
  onActiveConversationChange?: (conversation: ChatActiveConversation | null) => void;
  /** Drawer-only: fires when a streamed response settles (completed or failed). */
  onActivity?: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onMinimize?: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [drawerActiveId, setDrawerActiveId] = useState<string | null>(initialConversationId);
  const [selectedModelId, setSelectedModelId] = useState<AiModelId>(defaultModelId);
  const [isSending, setIsSending] = useState(false);
  const [consultingMessageId, setConsultingMessageId] = useState<string | null>(null);
  const [consultingToolName, setConsultingToolName] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isConversationDrawerOpen, setIsConversationDrawerOpen] = useState(false);
  const [isMobileUsageExpanded, setIsMobileUsageExpanded] = useState(false);
  const [isUsageExpanded, setIsUsageExpanded] = useState(false);
  const [isToolsDialogOpen, setIsToolsDialogOpen] = useState(false);
  const [isDrawerContextExpanded, setIsDrawerContextExpanded] = useState(false);
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorUpgradeUrl, setErrorUpgradeUrl] = useState<string | null>(null);
  const [thinkingEffort, setThinkingEffort] = useState<string | null>(defaultThinkingEffort);
  // Keep per-tool overrides so refetches do not reset manual toggles while
  // each renderer still follows its semantic default on a fresh page load.
  const [toolExpansionOverrides, setToolExpansionOverrides] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const toggleToolExpanded = useCallback((toolKey: string, defaultExpanded: boolean) => {
    setToolExpansionOverrides((current) => {
      const next = new Map(current);
      const currentExpanded = current.get(toolKey) ?? defaultExpanded;
      next.set(toolKey, !currentExpanded);
      return next;
    });
  }, []);
  const { models, getModel } = useOpenRouterModels();

  const {
    data: conversationsData,
    mutate: mutateConversations,
    isLoading: conversationsIsLoading,
    error: conversationsError,
  } = useSWR<ConversationsResponse>(isGuest ? null : '/api/chat/conversations', fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const { data: credits, mutate: mutateCredits } = useSWR<{
    monthlyCents: number;
    monthlyExpiresAt: string | null;
    purchasedCents: number;
  }>(isGuest ? null : '/api/chat/credits', fetcher, {
    revalidateOnFocus: false,
  });
  const conversations = conversationsData?.conversations ?? [];
  const conversationsIsEmpty = conversations.length === 0;
  const conversationsLoadFailed = Boolean(conversationsError);
  const canUseAdminModels = conversationsData?.isAdmin ?? isAdmin;
  const safeSelectedModelId = selectedModelId;
  const queryActiveId = variant === 'page' ? searchParams.get('conversation') : null;
  const activeId = variant === 'page' ? queryActiveId : drawerActiveId;
  const activePortfolioId = pathname.match(/^\/carteira\/(\d+)(?:\/|$)/)?.[1];
  const screenContext = resolveChatScreenContext(pathname);
  const surfaceCopy = getChatSurfaceCopy(variant, Boolean(activePortfolioId), screenContext);
  const quickPrompts = screenContext?.suggestions?.length
    ? screenContext.suggestions
    : getChatQuickPrompts(variant, Boolean(activePortfolioId));
  const messagesViewportRef = useRef<HTMLDivElement | null>(null);
  const messagesContentRef = useRef<HTMLDivElement | null>(null);
  const [sidebarPreferencesHydrated, setSidebarPreferencesHydrated] = useState(false);
  const shouldFollowMessagesRef = useRef(true);
  const previousActiveIdRef = useRef<string | null>(activeId);
  const autoCreateConversationAttemptedRef = useRef(false);
  const activeConversationSummary = conversations.find((conversation) => conversation.id === activeId);
  const { data: conversationData, mutate: mutateConversation } = useSWR<ConversationResponse>(
    activeId ? `/api/chat/conversations/${activeId}` : null,
    fetcher,
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );
  const activeConversation = conversationData?.conversation;
  const selectedModel = getModel(safeSelectedModelId);
  const hasNoCredits = credits !== undefined && credits.monthlyCents + credits.purchasedCents <= 0;
  const mobileContextSummary =
    selectedModel?.contextLength === null
      ? 'dinâmico'
      : activeConversation?.totalInputTokens == null
        ? '—'
        : `~${formatTokens(activeConversation.totalInputTokens)}`;

  useEffect(() => {
    if (variant !== 'page') {
      setSidebarPreferencesHydrated(false);
      return;
    }
    try {
      setLeftSidebarCollapsed(window.localStorage.getItem(LEFT_SIDEBAR_STORAGE_KEY) === 'true');
      setRightSidebarCollapsed(window.localStorage.getItem(RIGHT_SIDEBAR_STORAGE_KEY) === 'true');
    } catch {
      // Storage can be unavailable in privacy modes; open sidebars remain safe.
    }
    setSidebarPreferencesHydrated(true);
  }, [variant]);

  useEffect(() => {
    if (variant !== 'page' || !sidebarPreferencesHydrated) return;
    try {
      window.localStorage.setItem(LEFT_SIDEBAR_STORAGE_KEY, String(leftSidebarCollapsed));
      window.localStorage.setItem(RIGHT_SIDEBAR_STORAGE_KEY, String(rightSidebarCollapsed));
    } catch {
      // Storage is an enhancement, not a prerequisite for using the chat.
    }
  }, [leftSidebarCollapsed, rightSidebarCollapsed, sidebarPreferencesHydrated, variant]);

  useEffect(() => {
    setThinkingEffort(activeConversation?.thinkingEffort ?? defaultThinkingEffort);
  }, [activeConversation?.id, activeConversation?.thinkingEffort, defaultThinkingEffort]);

  useEffect(() => {
    if (!activeId || !selectedModel?.reasoning) return;
    const valid = reasoningOptions(selectedModel).some((option) => option.value === thinkingEffort);
    if (!valid && thinkingEffort !== null) {
      setThinkingEffort(null);
      void fetch(`/api/chat/conversations/${activeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thinkingEffort: null }),
      }).then(() => mutateConversation());
    }
  }, [activeId, selectedModel, thinkingEffort, mutateConversation]);

  const updateThinkingEffort = async (value: string | null) => {
    if (!activeId) return;
    setThinkingEffort(value);
    const response = await fetch(`/api/chat/conversations/${activeId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ thinkingEffort: value }),
    });
    if (response.ok) await mutateConversation();
  };

  useEffect(() => {
    if (!conversations.length) return;
    const currentIsValid = activeId && conversations.some((conversation) => conversation.id === activeId);
    if (currentIsValid) return;
    const nextId = conversations[0].id;
    if (variant === 'page') router.replace(`/chat?conversation=${encodeURIComponent(nextId)}`);
    else setDrawerActiveId(nextId);
  }, [activeId, conversations, router, variant]);

  // Report the active drawer conversation through a ref so the persistent
  // launcher can remember it without re-subscribing on every render.
  const onActiveConversationChangeRef = useRef(onActiveConversationChange);
  useEffect(() => {
    onActiveConversationChangeRef.current = onActiveConversationChange;
  });
  useEffect(() => {
    if (variant !== 'drawer') return;
    const title = activeConversation?.title;
    onActiveConversationChangeRef.current?.(activeId ? { id: activeId, title: title ?? 'Conversa' } : null);
  }, [variant, activeId, activeConversation?.id, activeConversation?.title]);

  const onActivityRef = useRef(onActivity);
  useEffect(() => {
    onActivityRef.current = onActivity;
  });

  const refresh = async () => {
    if (isSending) return;
    setIsRefreshing(true);
    try {
      await Promise.all([mutateConversations(), mutateConversation()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const createConversation = async () => {
    if (isGuest || isCreatingConversation) return;
    setError(null);
    setIsCreatingConversation(true);
    try {
      // Already sitting in a fresh, empty conversation: keep it instead of
      // stacking another empty one on every click of "Nova conversa".
      if (activeId && activeConversationSummary?.lastMessage == null) return;
      const response = await fetch('/api/chat/conversations', { method: 'POST' });
      if (!response.ok) {
        setError('Não foi possível criar a conversa.');
        return;
      }
      const data = (await response.json()) as {
        conversation: ConversationSummary;
      };
      await mutateConversations();
      if (variant === 'page') router.push(`/chat?conversation=${encodeURIComponent(data.conversation.id)}`);
      else setDrawerActiveId(data.conversation.id);
    } finally {
      setIsCreatingConversation(false);
    }
  };

  // When the /chat page opens with no conversations yet, create one
  // automatically so the composer is immediately usable. Guarded by a ref so
  // it fires at most once per mount (Strict Mode / re-renders safe) and never
  // retries if the creation fails — the manual "Nova conversa" button remains
  // the fallback.
  useEffect(() => {
    if (isGuest || variant !== 'page') return;
    if (conversationsIsLoading || conversationsLoadFailed) return;
    if (!conversationsIsEmpty) return;
    if (activeId) return;
    if (autoCreateConversationAttemptedRef.current) return;
    autoCreateConversationAttemptedRef.current = true;
    void createConversation();
  }, [
    activeId,
    conversationsIsEmpty,
    conversationsIsLoading,
    conversationsLoadFailed,
    createConversation,
    isGuest,
    variant,
  ]);

  const selectConversation = (conversationId: string) => {
    setError(null);
    if (variant === 'page') router.push(`/chat?conversation=${encodeURIComponent(conversationId)}`);
    else setDrawerActiveId(conversationId);
  };

  const selectMobileConversation = (conversationId: string) => {
    selectConversation(conversationId);
    setIsConversationDrawerOpen(false);
  };

  const updateConversationCache = async (
    updater: (conversation: ConversationResponse | undefined) => ConversationResponse | undefined,
  ) => {
    await mutateConversation(updater, false);
  };

  const sendMessage = async (content: string) => {
    if (isGuest || !activeId || isSending) return;
    // Re-enable auto-follow at the start of each submission so the optimistic
    // user message and the streaming response scroll into view even if the
    // user had scrolled slightly upward previously. Once they scroll manually
    // during the stream, handleMessagesScroll flips this back to false and
    // the auto-scroll yields control until the next send.
    shouldFollowMessagesRef.current = true;
    setIsSending(true);
    setError(null);
    const optimisticUserId = `optimistic-user-${Date.now()}`;
    const optimisticAssistantId = `optimistic-assistant-${Date.now()}`;
    const createdAt = new Date().toISOString();
    const userMessage: ChatMessage = {
      id: optimisticUserId,
      role: 'USER',
      status: 'COMPLETE',
      content,
      modelId: null,
      createdAt,
    };
    const assistantMessage: ChatMessage = {
      id: optimisticAssistantId,
      role: 'ASSISTANT',
      status: 'COMPLETE',
      content: '',
      modelId: safeSelectedModelId,
      createdAt,
    };

    await updateConversationCache((current) =>
      current?.conversation
        ? {
            conversation: {
              ...current.conversation,
              messages: [...current.conversation.messages, userMessage, assistantMessage],
            },
          }
        : current,
    );

    try {
      const response = await fetch(`/api/chat/conversations/${activeId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          modelId: safeSelectedModelId,
          ...(activePortfolioId ? { activePortfolioId } : {}),
          currentPathname: pathname,
        }),
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: unknown;
          upgradeUrl?: unknown;
        } | null;
        setErrorUpgradeUrl(
          response.status === 402 && typeof payload?.upgradeUrl === 'string' ? payload.upgradeUrl : null,
        );
        throw new Error(typeof payload?.error === 'string' ? payload.error : 'Não foi possível enviar a mensagem.');
      }

      let streamError: string | null = null;
      let streamCompleted = false;
      let pendingDelta = '';
      let pendingModelId: string | undefined;
      let pendingRenderers: RendererDescriptor[] = [];
      let cacheFlushPromise = Promise.resolve();
      let cacheFlushTimer: ReturnType<typeof setTimeout> | null = null;
      let cacheFlushInvalidated = false;
      const flushPendingCache = () => {
        cacheFlushTimer = null;
        const delta = pendingDelta;
        const modelId = pendingModelId;
        // Renderers stream in as soon as each tool finishes so the real data
        // appears while the text is still being generated. The list only
        // grows, so every flush writes the full accumulated set.
        const renderers = pendingRenderers.length ? [...pendingRenderers] : undefined;
        pendingDelta = '';
        pendingModelId = undefined;
        if (!delta && !modelId && !renderers?.length) return;
        cacheFlushPromise = cacheFlushPromise.then(() => {
          if (cacheFlushInvalidated) return;
          return updateConversationCache((current) =>
            current?.conversation
              ? {
                  conversation: {
                    ...current.conversation,
                    messages: current.conversation.messages.map((message) =>
                      message.id === optimisticAssistantId
                        ? {
                            ...message,
                            content: `${message.content}${delta}`,
                            ...(modelId ? { modelId } : {}),
                            ...(renderers?.length ? { renderers } : {}),
                          }
                        : message,
                    ),
                  },
                }
              : current,
          );
        });
      };
      const scheduleCacheFlush = () => {
        if (cacheFlushTimer !== null) return;
        cacheFlushTimer = setTimeout(flushPendingCache, 50);
      };
      const invalidateCacheFlush = () => {
        cacheFlushInvalidated = true;
        pendingDelta = '';
        pendingModelId = undefined;
        if (cacheFlushTimer !== null) {
          clearTimeout(cacheFlushTimer);
          cacheFlushTimer = null;
        }
      };

      try {
        await readSseStream(response, (event, data) => {
          if (!cacheFlushInvalidated && event === 'delta' && typeof data.content === 'string') {
            pendingDelta += data.content;
            scheduleCacheFlush();
          }
          if (!cacheFlushInvalidated && event === 'complete' && typeof data.modelId === 'string') {
            pendingModelId = data.modelId;
            scheduleCacheFlush();
          }
          if (event === 'tool_start') {
            setConsultingMessageId(optimisticAssistantId);
            setConsultingToolName(typeof data.name === 'string' ? data.name : null);
          }
          if (event === 'renderer' && data.renderer) {
            const renderer = data.renderer as RendererDescriptor;
            if (typeof renderer.kind === 'string' && renderer.params) {
              pendingRenderers.push(renderer);
              scheduleCacheFlush();
            }
          }
          if (event === 'complete') {
            streamCompleted = true;
            setConsultingMessageId(null);
            setConsultingToolName(null);
            scheduleCacheFlush();
          }
          if (event === 'error' && typeof data.message === 'string') {
            streamError =
              typeof data.requestId === 'string' && data.requestId
                ? `${data.message}\n\nID do erro: ${data.requestId}`
                : data.message;
            setConsultingMessageId(null);
            setConsultingToolName(null);
            invalidateCacheFlush();
          }
        });
      } catch (streamReadError) {
        invalidateCacheFlush();
        await cacheFlushPromise;
        throw streamReadError;
      }
      if (streamError) {
        invalidateCacheFlush();
      } else if (!streamCompleted) {
        invalidateCacheFlush();
        throw new Error('A resposta foi interrompida antes de ser concluída. Tente novamente.');
      } else if (cacheFlushTimer !== null) {
        clearTimeout(cacheFlushTimer);
        flushPendingCache();
      }
      await cacheFlushPromise;
      if (streamError) throw new Error(streamError);

      if (activeConversationSummary?.title === 'Nova conversa') {
        const title = makeTitle(content);
        await fetch(`/api/chat/conversations/${activeId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
        });
      }
      await Promise.all([mutateConversation(), mutateConversations(), mutateCredits()]);
      onActivityRef.current?.();
      if (pendingRenderers.length) {
        await updateConversationCache((current) =>
          current?.conversation
            ? (() => {
                const lastAssistant = [...current.conversation.messages]
                  .reverse()
                  .find((message) => message.role === 'ASSISTANT');
                return {
                  conversation: {
                    ...current.conversation,
                    messages: current.conversation.messages.map((message) =>
                      message.id === lastAssistant?.id ? { ...message, renderers: pendingRenderers } : message,
                    ),
                  },
                };
              })()
            : current,
        );
      }
    } catch (sendError) {
      const message = sendError instanceof Error ? sendError.message : 'Não foi possível enviar a mensagem.';
      setError(message);
      await Promise.all([mutateConversation(), mutateConversations(), mutateCredits()]);
      onActivityRef.current?.();
    } finally {
      setIsSending(false);
      setConsultingMessageId(null);
    }
  };

  const renameActiveConversation = async () => {
    if (!activeId || !activeConversation) return;
    const title = window.prompt('Nome da conversa', activeConversation.title)?.trim();
    if (!title || title === activeConversation.title) return;
    const response = await fetch(`/api/chat/conversations/${activeId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    if (!response.ok) {
      setError('Não foi possível renomear a conversa.');
      return;
    }
    await Promise.all([
      updateConversationCache((current) =>
        current?.conversation ? { conversation: { ...current.conversation, title } } : current,
      ),
      mutateConversations(),
    ]);
  };

  const deleteActiveConversation = async () => {
    if (!activeId || !window.confirm('Excluir esta conversa e todas as mensagens?')) return;
    const response = await fetch(`/api/chat/conversations/${activeId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      setError('Não foi possível excluir a conversa.');
      return;
    }
    await mutateConversation(undefined, false);
    await mutateConversations();
    if (variant === 'page') router.replace('/chat');
    else setDrawerActiveId(null);
  };

  const messages = activeConversation?.messages ?? [];

  // Actions for the latest assistant response: navigation/follow-up chips
  // derived from the tools' renderers plus the current screen's actions.
  const responseActions = useMemo(() => {
    if (isSending) return [];
    const lastAssistant = [...messages].reverse().find((message) => message.role === 'ASSISTANT');
    if (!lastAssistant) return [];
    return getResponseActions(lastAssistant.renderers ?? [], screenContext?.actions);
  }, [messages, isSending, screenContext]);

  useLayoutEffect(() => {
    const conversationChanged = previousActiveIdRef.current !== activeId;
    previousActiveIdRef.current = activeId;

    if (conversationChanged) shouldFollowMessagesRef.current = true;
    if (!shouldFollowMessagesRef.current) return;

    const frame = window.requestAnimationFrame(() => {
      const viewport = messagesViewportRef.current;
      if (!viewport || !shouldFollowMessagesRef.current) return;
      viewport.scrollTop = viewport.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeId, messages]);

  useEffect(() => {
    const content = messagesContentRef.current;
    if (!content) return;

    const scheduler = createMessagesAutoScrollScheduler({
      viewport: messagesViewportRef as { current: ScrollViewport | null },
      shouldFollow: shouldFollowMessagesRef,
      content: { current: content },
      globals: {
        ResizeObserver: typeof ResizeObserver === 'undefined' ? undefined : ResizeObserver,
        requestAnimationFrame: window.requestAnimationFrame.bind(window),
        cancelAnimationFrame: window.cancelAnimationFrame.bind(window),
        setTimeout,
        clearTimeout,
      },
    });

    return () => {
      scheduler.cancel();
    };
  }, [activeId, messages]);

  const handleMessagesScroll = () => {
    const viewport = messagesViewportRef.current;
    if (!viewport) return;
    const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    shouldFollowMessagesRef.current = distanceFromBottom <= CHAT_SCROLL_BOTTOM_THRESHOLD;
  };

  const openInChat = () => {
    const destination = activeId ? `/chat?conversation=${encodeURIComponent(activeId)}` : '/chat';
    onClose?.();
    router.push(destination);
  };

  if (isGuest) return <GuestChatWorkspace />;

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        display: 'grid',
        gridTemplateColumns:
          variant === 'page'
            ? {
                xs: '1fr',
                md: `${leftSidebarCollapsed ? 32 : 260}px minmax(0, 1fr) ${rightSidebarCollapsed ? 32 : 280}px`,
                xl: `${leftSidebarCollapsed ? 32 : 300}px minmax(0, 1fr) ${rightSidebarCollapsed ? 32 : 320}px`,
              }
            : '1fr',
      }}
      data-chat-page={variant === 'page' ? 'true' : undefined}
      data-chat-surface={variant}
    >
      {variant === 'page' ? (
        <ConversationList
          conversations={conversations}
          activeId={activeId}
          onSelect={selectConversation}
          onNew={() => void createConversation()}
          onRefresh={() => void refresh()}
          isRefreshing={isRefreshing}
          collapsed={leftSidebarCollapsed}
          onToggleCollapsed={() => setLeftSidebarCollapsed((value) => !value)}
          credits={credits}
        />
      ) : null}

      {variant === 'page' ? (
        <Drawer
          anchor='left'
          open={isConversationDrawerOpen}
          onClose={() => setIsConversationDrawerOpen(false)}
          sx={{ display: { xs: 'block', md: 'none' } }}
          slotProps={{
            paper: {
              sx: {
                width: 'min(88vw, 340px)',
                bgcolor: 'hsl(var(--background))',
                color: 'hsl(var(--foreground))',
              },
            },
          }}
          ModalProps={{ keepMounted: true }}
          aria-labelledby='mobile-conversations-title'
        >
          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ px: 2.5, py: 1.5 }}>
            <Typography
              id='mobile-conversations-title'
              variant='h6'
              sx={{ fontFamily: 'var(--font-display)', lineHeight: 1.1 }}
            >
              Conversas
            </Typography>
            <Tooltip title='Fechar conversas'>
              <IconButton onClick={() => setIsConversationDrawerOpen(false)} aria-label='Fechar conversas'>
                <X size={18} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Divider />
          <ConversationList
            conversations={conversations}
            activeId={activeId}
            onSelect={selectMobileConversation}
            onNew={() => {
              setIsConversationDrawerOpen(false);
              void createConversation();
            }}
            onRefresh={() => void refresh()}
            isRefreshing={isRefreshing}
            collapsed={false}
            onToggleCollapsed={() => undefined}
            mobile
            showCollapseButton={false}
            credits={credits}
          />
        </Drawer>
      ) : null}

      <Box
        sx={{
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{
            px: variant === 'drawer' ? 2 : { xs: 2, md: 3.5 },
            py: 1.75,
            borderBottom: '1px solid hsl(var(--border))',
            overflow: 'hidden',
          }}
        >
          <Stack direction='row' alignItems='center' gap={{ xs: 0.5, md: 1 }} sx={{ flex: 1, minWidth: 0 }}>
            {variant === 'page' ? (
              <Tooltip title='Conversas'>
                <IconButton
                  onClick={() => setIsConversationDrawerOpen(true)}
                  aria-label='Abrir conversas'
                  sx={{
                    display: { xs: 'inline-flex', md: 'none' },
                    flexShrink: 0,
                  }}
                >
                  <ListIcon size={19} />
                </IconButton>
              </Tooltip>
            ) : null}
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: variant === 'drawer' ? 38 : 34,
                height: variant === 'drawer' ? 38 : 34,
                borderRadius: '50%',
                bgcolor: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
              }}
            >
              <Sparkles size={variant === 'drawer' ? 19 : 17} />
            </Box>
            <Box minWidth={0} sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography variant='body2' noWrap sx={{ fontWeight: 850 }}>
                {variant === 'drawer' ? surfaceCopy.title : (activeConversation?.title ?? surfaceCopy.title)}
              </Typography>
              {variant === 'page' ? (
                <Typography
                  variant='caption'
                  noWrap
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    color: 'hsl(var(--muted-foreground))',
                  }}
                >
                  {activeId
                    ? `Modelo da próxima mensagem: ${modelLabel(selectedModelId, models)}`
                    : 'Escolha uma conversa para começar'}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <Stack direction='row' spacing={0.25} sx={{ flexShrink: 0 }}>
            {variant === 'page' ? (
              <Tooltip title='Nova conversa'>
                <IconButton
                  aria-label='Nova conversa'
                  onClick={() => void createConversation()}
                  disabled={isCreatingConversation}
                  size='small'
                >
                  <MessageSquarePlus size={17} />
                </IconButton>
              </Tooltip>
            ) : null}
            {variant === 'page' && activeId ? (
              <>
                <Tooltip title='Renomear conversa'>
                  <IconButton
                    aria-label='Renomear conversa'
                    onClick={() => void renameActiveConversation()}
                    size='small'
                  >
                    <Pencil size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Excluir conversa'>
                  <IconButton
                    aria-label='Excluir conversa'
                    onClick={() => void deleteActiveConversation()}
                    size='small'
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
            {variant === 'drawer' ? (
              <>
                <Tooltip title={isExpanded ? 'Recolher janela' : 'Expandir janela'}>
                  <IconButton
                    onClick={onToggleExpand}
                    aria-label={isExpanded ? 'Recolher janela do Copiloto' : 'Expandir janela do Copiloto'}
                    size='small'
                  >
                    {isExpanded ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title='Abrir Copiloto completo'>
                  <IconButton onClick={openInChat} aria-label='Abrir Copiloto completo' size='small'>
                    <ExternalLink size={17} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Minimizar'>
                  <IconButton onClick={onMinimize ?? onClose} aria-label='Minimizar Copiloto' size='small'>
                    <Minus size={18} />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={onClose} aria-label='Fechar Copiloto' size='small'>
                  <X size={18} />
                </IconButton>
              </>
            ) : null}
          </Stack>
        </Stack>

        {variant === 'drawer' ? (
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid hsl(var(--border))' }}>
            <ButtonBase
              onClick={() => setIsDrawerContextExpanded((expanded) => !expanded)}
              aria-expanded={isDrawerContextExpanded}
              sx={{
                width: '100%',
                borderRadius: 1.5,
                px: 1,
                py: 0.5,
                textAlign: 'left',
                '&:hover': { bgcolor: 'hsl(var(--muted))' },
              }}
            >
              <Stack direction='row' spacing={0.75} alignItems='center' sx={{ width: '100%' }}>
                <Sparkles size={14} style={{ flexShrink: 0, color: 'hsl(var(--primary))' }} />
                <Typography variant='caption' noWrap sx={{ flex: 1, fontWeight: 800 }}>
                  Contexto: {surfaceCopy.contextLabel}
                </Typography>
                <ChevronDown
                  size={15}
                  style={{
                    flexShrink: 0,
                    transform: isDrawerContextExpanded ? 'rotate(180deg)' : undefined,
                    transition: 'transform 0.15s',
                  }}
                />
              </Stack>
            </ButtonBase>
            {isDrawerContextExpanded ? (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <ChatContextCard variant='drawer' activePortfolioId={activePortfolioId} screenContext={screenContext} />
                <Paper variant='outlined' sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
                    <Typography variant='caption' sx={{ display: 'block', fontWeight: 850 }}>
                      Créditos de IA
                    </Typography>
                    <Link
                      href='/configuracoes/faturamento#adicionar-creditos'
                      style={{ fontSize: 12, fontWeight: 700, color: 'hsl(var(--primary))' }}
                    >
                      Comprar créditos
                    </Link>
                  </Stack>
                  <CreditBalanceSummary credits={credits} compact />
                </Paper>
              </Stack>
            ) : null}
          </Box>
        ) : null}

        {variant === 'page' ? (
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              px: 2,
              pt: 1.5,
            }}
          >
            <ButtonBase
              component='button'
              type='button'
              onClick={() => setIsMobileUsageExpanded((expanded) => !expanded)}
              aria-expanded={isMobileUsageExpanded}
              aria-controls={MOBILE_USAGE_PANEL_ID}
              sx={{
                display: 'block',
                width: '100%',
                borderRadius: 2,
                textAlign: 'left',
                '&:focus-visible': {
                  outline: '2px solid hsl(var(--ring))',
                  outlineOffset: 2,
                },
              }}
            >
              <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 2, bgcolor: 'hsl(var(--card))' }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Contexto ativo
                    </Typography>
                    <Typography variant='body2' fontWeight={700} noWrap>
                      {surfaceCopy.contextLabel} · {modelLabel(safeSelectedModelId, models)} · {mobileContextSummary}
                    </Typography>
                  </Box>
                  <ChevronDown
                    size={18}
                    aria-hidden='true'
                    style={{
                      flexShrink: 0,
                      transform: isMobileUsageExpanded ? 'rotate(180deg)' : undefined,
                    }}
                  />
                </Stack>
              </Paper>
            </ButtonBase>
            {isMobileUsageExpanded ? (
              <Paper
                id={MOBILE_USAGE_PANEL_ID}
                variant='outlined'
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'hsl(var(--card))',
                }}
              >
                <ModelPicker
                  value={safeSelectedModelId}
                  onChange={setSelectedModelId}
                  models={models}
                  compact
                  isAdmin={canUseAdminModels}
                />
                <ContextMeter
                  modelId={safeSelectedModelId}
                  models={models}
                  lastInputTokens={activeConversation?.totalInputTokens}
                />
                <ConversationUsageDisclosure
                  expanded={isUsageExpanded}
                  onToggle={() => setIsUsageExpanded((value) => !value)}
                  inputTokens={activeConversation?.totalInputTokens}
                  outputTokens={activeConversation?.totalOutputTokens}
                  cachedInputTokens={activeConversation?.totalCachedInputTokens}
                  cacheWriteTokens={activeConversation?.totalCacheWriteTokens}
                  costUsd={activeConversation?.totalCostUsd}
                  costBrl={activeConversation?.totalCostBrl}
                />
                <Divider sx={{ my: 1.25 }} />
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 800 }}>
                    Créditos IA
                  </Typography>
                  <CreditBalanceSummary credits={credits} compact />
                  <Button
                    component={Link}
                    href='/configuracoes/faturamento#adicionar-creditos'
                    size='small'
                    startIcon={<CreditCard size={14} aria-hidden='true' />}
                    sx={{ mt: 0.75, px: 0, textTransform: 'none' }}
                  >
                    Comprar créditos
                  </Button>
                </Box>
              </Paper>
            ) : null}
          </Box>
        ) : null}

        {hasNoCredits ? (
          <Alert
            severity='info'
            sx={{
              mx: { xs: 2, md: 3.5 },
              mb: 1,
              alignItems: { xs: 'flex-start', sm: 'center' },
            }}
            action={
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={0.5}>
                <Button
                  component={Link}
                  href='/configuracoes/faturamento#adicionar-creditos'
                  size='small'
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Comprar créditos
                </Button>
                <Button
                  component={Link}
                  href='/precos'
                  size='small'
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Conhecer o Pro
                </Button>
              </Stack>
            }
          >
            <Typography variant='body2' fontWeight={750}>
              Seu saldo de IA está zerado. Adicione créditos ou conheça o Pro para continuar usando o Copiloto.
            </Typography>
          </Alert>
        ) : null}

        {error ? (
          <Alert severity='error' onClose={() => setError(null)} sx={{ mx: { xs: 2, md: 3.5 }, mb: 1 }}>
            {error}
            {errorUpgradeUrl ? (
              <Button component={Link} href={errorUpgradeUrl} size='small' sx={{ ml: 1 }}>
                Ver saldo e créditos
              </Button>
            ) : null}
          </Alert>
        ) : null}

        <Box
          ref={messagesViewportRef}
          onScroll={handleMessagesScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: variant === 'drawer' ? 2 : { xs: 2, md: 3.5 },
            py: variant === 'drawer' ? 2 : { xs: 2.5, md: 3 },
          }}
        >
          <Box
            ref={messagesContentRef}
            sx={{ minHeight: '100%', width: '100%', maxWidth: variant === 'drawer' ? '100%' : 920, mx: 'auto' }}
          >
            {messages.length ? (
              <>
                {messages.map((message) => (
                  <ChatMessageBubble
                    key={message.id}
                    message={message}
                    variant={variant}
                    isConsulting={message.id === consultingMessageId}
                    consultingToolName={message.id === consultingMessageId ? consultingToolName : null}
                    models={models}
                    toolExpansionOverrides={toolExpansionOverrides}
                    onToggleTool={toggleToolExpanded}
                    onAsk={(prompt) => void sendMessage(prompt)}
                  />
                ))}
                {responseActions.length ? (
                  <Stack
                    direction='row'
                    useFlexGap
                    flexWrap='wrap'
                    spacing={1}
                    sx={{ mt: 0.5, mb: 1.5 }}
                    data-chat-response-actions
                  >
                    {responseActions.map((action) =>
                      action.href ? (
                        <Button
                          key={action.label}
                          size='small'
                          variant='outlined'
                          endIcon={<ArrowUpRight size={14} />}
                          onClick={() => router.push(action.href as string)}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
                        >
                          {action.label}
                        </Button>
                      ) : (
                        <Button
                          key={action.label}
                          size='small'
                          variant='outlined'
                          onClick={() => void sendMessage(action.prompt as string)}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
                        >
                          {action.label}
                        </Button>
                      ),
                    )}
                  </Stack>
                ) : null}
              </>
            ) : (
              <Box sx={{ maxWidth: 600, pt: { xs: 2, md: 8 } }}>
                <Typography variant='h5' sx={{ fontFamily: 'var(--font-display)' }}>
                  Por onde começamos?
                </Typography>
                <Typography variant='body2' sx={{ mt: 1, color: 'hsl(var(--muted-foreground))' }}>
                  Crie uma conversa e faça uma pergunta sobre um fundo, um indicador ou uma ideia para sua carteira.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <ChatQuickPrompts
                    prompts={getChatWelcomePrompts()}
                    disabled={!activeId || isSending}
                    onAsk={(prompt) => void sendMessage(prompt)}
                  />
                </Box>
                {!activeId ? (
                  <Button
                    variant='contained'
                    startIcon={<MessageSquarePlus size={16} />}
                    onClick={() => void createConversation()}
                    sx={{ mt: 2, textTransform: 'none' }}
                  >
                    Nova conversa
                  </Button>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            px: variant === 'drawer' ? 2 : { xs: 2, md: 3.5 },
            pb: variant === 'drawer' ? 2 : { xs: 2, md: 3 },
            width: '100%',
            maxWidth: variant === 'drawer' ? '100%' : 990,
            mx: 'auto',
          }}
        >
          <ChatQuickPrompts
            prompts={quickPrompts}
            disabled={!activeId || isSending}
            onAsk={(prompt) => void sendMessage(prompt)}
          />
          <ChatComposer
            onSubmit={(content) => void sendMessage(content)}
            disabled={!activeId}
            isSending={isSending}
            thinkingEffort={thinkingEffort}
            selectedModel={selectedModel}
            onThinkingChange={(value) => void updateThinkingEffort(value)}
            onOpenTools={() => setIsToolsDialogOpen(true)}
            variant={variant}
          />
        </Box>
      </Box>

      {variant === 'page' ? (
        <Box
          sx={{
            borderLeft: '1px solid hsl(var(--border))',
            display: { xs: 'none', md: 'block' },
            bgcolor: 'hsl(var(--muted) / 0.2)',
            position: 'relative',
            p: 0,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {rightSidebarCollapsed ? (
            <Box sx={{ position: 'relative', pb: 1 }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
                <SidebarCollapseButton side='right' collapsed onClick={() => setRightSidebarCollapsed(false)} />
              </Box>
              <Tooltip
                title={`Modelo: ${modelLabel(safeSelectedModelId, models)} · Créditos mensais: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((credits?.monthlyCents ?? 0) / 100)} · Créditos comprados: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((credits?.purchasedCents ?? 0) / 100)}`}
                placement='left'
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 5.5,
                    color: 'hsl(var(--muted-foreground))',
                  }}
                >
                  <Coins size={17} aria-hidden='true' />
                </Box>
              </Tooltip>
            </Box>
          ) : (
            <Stack sx={{ height: '100%', minHeight: 0 }}>
              <Stack
                sx={{
                  position: 'relative',
                  p: 1.5,
                  pb: 1,
                  borderBottom: '1px solid hsl(var(--border))',
                }}
              >
                <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
                  <SidebarCollapseButton
                    side='right'
                    collapsed={false}
                    onClick={() => setRightSidebarCollapsed(true)}
                  />
                </Box>
                <Box minWidth={0} sx={{ pl: 3 }}>
                  <Typography variant='body2' sx={{ fontWeight: 850 }}>
                    Contexto da conversa
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Lente ativa e ferramentas
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={1.75} sx={{ p: 1.5, pt: 1, minHeight: 0, overflowY: 'auto' }}>
                <ChatContextCard variant='page' activePortfolioId={activePortfolioId} screenContext={screenContext} />
                <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 2, bgcolor: 'hsl(var(--card))' }}>
                  <Typography variant='body2' sx={{ mb: 1, fontWeight: 850 }}>
                    Modelo de IA
                  </Typography>
                  <Stack direction='row' alignItems='flex-end' spacing={0.75}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <ModelPicker
                        value={safeSelectedModelId}
                        onChange={setSelectedModelId}
                        models={models}
                        isAdmin={canUseAdminModels}
                      />
                    </Box>
                    <Tooltip title='Definir modelo padrão'>
                      <IconButton
                        component={Link}
                        href='/configuracoes/modelo-ia'
                        aria-label='Definir modelo padrão'
                        size='small'
                        sx={{
                          mb: 0.25,
                          color: 'hsl(var(--muted-foreground))',
                          '&:hover': {
                            color: 'hsl(var(--foreground))',
                            bgcolor: 'hsl(var(--muted))',
                          },
                          '&:focus-visible': {
                            outline: '2px solid hsl(var(--ring))',
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <Settings2 size={17} aria-hidden='true' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <ContextMeter
                    modelId={safeSelectedModelId}
                    models={models}
                    lastInputTokens={activeConversation?.totalInputTokens}
                  />
                  <ConversationUsageDisclosure
                    expanded={isUsageExpanded}
                    onToggle={() => setIsUsageExpanded((value) => !value)}
                    inputTokens={activeConversation?.totalInputTokens}
                    outputTokens={activeConversation?.totalOutputTokens}
                    cachedInputTokens={activeConversation?.totalCachedInputTokens}
                    cacheWriteTokens={activeConversation?.totalCacheWriteTokens}
                    costUsd={activeConversation?.totalCostUsd}
                    costBrl={activeConversation?.totalCostBrl}
                  />
                </Paper>
                <ChatToolsPanel onOpenTools={() => setIsToolsDialogOpen(true)} />
              </Stack>
            </Stack>
          )}
        </Box>
      ) : null}
      <ChatToolsDialog open={isToolsDialogOpen} onClose={() => setIsToolsDialogOpen(false)} />
    </Box>
  );
}
